import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  User, 
  Shield, 
  Users, 
  Building2, 
  MapPin, 
  BookOpen,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  RefreshCw,
  Key,
  Crown,
  Briefcase
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

//dummy push test
const PermissionBrowser = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [permissionFilter, setPermissionFilter] = useState('');

  // Fetch users on search
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/dev/user-permissions/', {
        params: { search: searchTerm, limit: 50 }
      });
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchUsers]);

  // Fetch user details
  const fetchUserDetails = async (user) => {
    setSelectedUser(user);
    setDetailsLoading(true);
    try {
      const response = await api.post('/api/dev/user-permissions/', {
        user_id: user.id
      });
      if (response.data.success) {
        setUserDetails(response.data);
        // Expand all groups by default
        const expanded = {};
        response.data.groups.forEach(g => {
          expanded[g.id] = true;
        });
        setExpandedGroups(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to fetch user permissions');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Filter permissions based on search
  const filterPermissions = (permissions) => {
    if (!permissionFilter) return permissions;
    const filter = permissionFilter.toLowerCase();
    return permissions.filter(p => 
      p.codename?.toLowerCase().includes(filter) ||
      p.name?.toLowerCase().includes(filter) ||
      p.full_name?.toLowerCase().includes(filter) ||
      (typeof p === 'string' && p.toLowerCase().includes(filter))
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Key className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-gray-800">Permission Browser</h3>
        </div>
        <button
          onClick={fetchUsers}
          className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List Panel */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-4">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No users found
              </div>
            ) : (
              users.map(user => (
                <motion.button
                  key={user.id}
                  onClick={() => fetchUserDetails(user)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    selectedUser?.id === user.id
                      ? 'bg-purple-100 border-2 border-purple-500'
                      : 'bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                  }`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${
                      user.is_superuser ? 'bg-yellow-100' : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      {user.is_superuser ? (
                        <Crown className="w-4 h-4 text-yellow-600" />
                      ) : (
                        <User className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.email}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">{user.role || 'No role'}</span>
                        {user.is_internal && (
                          <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                            Internal
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))
            )}
          </div>
        </div>

        {/* Details Panel */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
          {!selectedUser ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-gray-500">
              <Shield className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a user to view permissions</p>
              <p className="text-sm">Click on a user from the list to see their details</p>
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : userDetails ? (
            <div className="space-y-6">
              {/* User Info Header */}
              <div className="flex items-start justify-between pb-4 border-b">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${
                    userDetails.user.is_superuser ? 'bg-yellow-100' : 'bg-purple-100'
                  }`}>
                    {userDetails.user.is_superuser ? (
                      <Crown className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <User className="w-6 h-6 text-purple-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{userDetails.user.email}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 rounded">
                        {userDetails.user.role || 'No role'}
                      </span>
                      {userDetails.user.is_superuser && (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
                          Superuser
                        </span>
                      )}
                      {userDetails.user.is_staff && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                          Staff
                        </span>
                      )}
                      {userDetails.user.is_internal && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                          Internal
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{userDetails.summary.total_permission_count}</p>
                  <p className="text-xs text-purple-700">Total Permissions</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{userDetails.summary.group_count}</p>
                  <p className="text-xs text-blue-700">Groups</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{userDetails.summary.sponsor_count}</p>
                  <p className="text-xs text-green-700">Sponsors</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">{userDetails.summary.site_count}</p>
                  <p className="text-xs text-orange-700">Sites</p>
                </div>
              </div>

              {/* Permission Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter permissions..."
                  value={permissionFilter}
                  onChange={(e) => setPermissionFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Tabs */}
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {/* Groups with Permissions */}
                {userDetails.groups.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                      <Users className="w-4 h-4" />
                      <span>Groups ({userDetails.groups.length})</span>
                    </div>
                    {userDetails.groups.map(group => (
                      <div key={group.id} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleGroup(group.id)}
                          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            {expandedGroups[group.id] ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <span className="font-medium text-gray-800">{group.name}</span>
                            <span className="text-xs text-gray-500">
                              ({filterPermissions(group.permissions).length} permissions)
                            </span>
                          </div>
                        </button>
                        <AnimatePresence>
                          {expandedGroups[group.id] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="p-3 bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                  {filterPermissions(group.permissions).map((perm, idx) => (
                                    <div
                                      key={idx}
                                      className="text-xs px-2 py-1 bg-gray-50 dark:bg-gray-900 rounded flex items-center space-x-1"
                                    >
                                      <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                                      <span className="text-gray-700 truncate" title={perm.full_name}>
                                        {perm.codename}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                )}

                {/* Direct Permissions */}
                {userDetails.direct_permissions.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                      <Key className="w-4 h-4" />
                      <span>Direct Permissions ({userDetails.direct_permissions.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-3 border rounded-lg bg-white dark:bg-gray-800">
                      {filterPermissions(userDetails.direct_permissions).map((perm, idx) => (
                        <div
                          key={idx}
                          className="text-xs px-2 py-1 bg-purple-50 rounded flex items-center space-x-1"
                        >
                          <Check className="w-3 h-3 text-purple-500 flex-shrink-0" />
                          <span className="text-gray-700 truncate" title={perm.full_name}>
                            {perm.codename}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sponsors */}
                {userDetails.sponsors.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                      <Building2 className="w-4 h-4" />
                      <span>Sponsors ({userDetails.sponsors.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-white dark:bg-gray-800">
                      {userDetails.sponsors.map(sponsor => (
                        <span
                          key={sponsor.id}
                          className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded"
                        >
                          {sponsor.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sites */}
                {userDetails.sites.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                      <MapPin className="w-4 h-4" />
                      <span>Sites ({userDetails.sites.length})</span>
                    </div>
                    <div className="space-y-1 p-3 border rounded-lg bg-white dark:bg-gray-800 max-h-40 overflow-y-auto">
                      {userDetails.sites.map(site => (
                        <div
                          key={site.id}
                          className="text-xs px-2 py-1 bg-orange-50 rounded flex items-center justify-between"
                        >
                          <span className="text-gray-700">{site.name}</span>
                          {site.study && (
                            <span className="text-gray-500 text-xs">{site.study}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Studies */}
                {userDetails.studies.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                      <BookOpen className="w-4 h-4" />
                      <span>Studies ({userDetails.studies.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-white dark:bg-gray-800">
                      {userDetails.studies.map(study => (
                        <span
                          key={study.id}
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded"
                        >
                          {study.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Permissions (collapsed by default) */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <Shield className="w-4 h-4" />
                    <span>All Permissions ({filterPermissions(userDetails.all_permissions).length})</span>
                  </div>
                  <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-900 max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {filterPermissions(userDetails.all_permissions).map((perm, idx) => (
                        <div
                          key={idx}
                          className="text-xs px-2 py-1 bg-white dark:bg-gray-800 rounded text-gray-600 truncate"
                          title={perm}
                        >
                          {perm}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PermissionBrowser;
